import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useAuth } from "../../context/AuthContext";
import { useCandidatoContext } from "../../context/CandidatoContext";

const ROTULOS_ROLE: Record<string, string> = {
  admin: "Admin",
  candidato: "Candidato",
  lideranca: "Liderança",
  assessor: "Assessor",
};

const SIGLAS_ROLE: Record<string, string> = {
  admin: "[ADM]",
  candidato: "[CAN]",
  lideranca: "[LID]",
  assessor: "[ASS]",
};

function iniciais(nome?: string) {
  return (nome || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

interface TestUser {
  id: number;
  nome: string;
  role: string;
  is_teste: boolean;
}

export default function UserDropdown() {
  const { user, logout } = useAuth();
  const { selecionado } = useCandidatoContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [testUsers, setTestUsers] = useState<TestUser[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);

  useEffect(() => {
    if (isOpen && user?.role === "admin") {
      setCarregandoUsuarios(true);
      fetch("/api/usuarios", { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            setTestUsers(data.filter((u) => u.is_teste));
          }
        })
        .catch((err) => console.error("Erro ao carregar usuários de teste", err))
        .finally(() => setCarregandoUsuarios(false));
    }
  }, [isOpen, user?.role]);

  if (!user) return null;

  const sair = async () => {
    await logout();
    navigate("/login");
  };

  const simularUsuario = async (targetUserId: number) => {
    try {
      const res = await fetch("/api/auth/impersonate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetUserId }),
      });
      if (res.ok) {
        setIsOpen(false);
        window.location.href = "/dashboard";
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.erro || "Falha ao simular usuário");
      }
    } catch (err) {
      console.error(err);
      alert("Erro ao conectar com o servidor");
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center text-gray-700 dropdown-toggle dark:text-gray-400"
      >
        <span className="mr-3 flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-sm font-bold text-white">
          {iniciais(user.nome)}
        </span>
        <span className="mr-1 text-left">
          <span className="block font-medium text-theme-sm">{user.nome}</span>
          {user.cargo && (
            <span className="block text-xs text-gray-500 dark:text-gray-400">{user.cargo}</span>
          )}
        </span>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="absolute right-0 mt-[17px] flex w-[220px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      >
        <div className="border-b border-gray-200 pb-3 dark:border-gray-800">
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
            {user.nome}
          </span>
          <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
            {ROTULOS_ROLE[user.role] || user.role}
          </span>
        </div>
        {user.role === "admin" && (
          <div className="border-b border-gray-200 py-3 dark:border-gray-800">
            <span className="block text-theme-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Simular Contas (Teste)
            </span>
            {carregandoUsuarios ? (
              <span className="block text-theme-xs text-gray-500 dark:text-gray-400">Carregando...</span>
            ) : testUsers.length === 0 ? (
              <span className="block text-theme-xs text-gray-500 dark:text-gray-400">Nenhum candidato/assessor</span>
            ) : (
              <div className="max-h-[160px] overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                {testUsers.map((tu) => (
                  <button
                    key={tu.id}
                    onClick={() => simularUsuario(tu.id)}
                    className="w-full text-left rounded px-2 py-1 text-theme-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white truncate block"
                    title={`Entrar como ${tu.nome} (${tu.role})`}
                  >
                    <span className="font-semibold text-brand-600 dark:text-brand-400 mr-1 uppercase">
                      {SIGLAS_ROLE[tu.role] || `[${tu.role.substring(0, 3).toUpperCase()}]`}
                    </span>
                    {tu.nome}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {(user.role === "candidato" || (user.role === "admin" && selecionado)) && (
          <Link
            to="/candidatos"
            onClick={() => setIsOpen(false)}
            className="mt-3 flex items-center gap-3 rounded-lg px-3 py-2 text-left font-medium text-gray-700 text-theme-sm hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/5"
          >
            {user.role === "admin" ? "Dados do candidato" : "Editar meus dados"}
          </Link>
        )}
        <button
          onClick={sair}
          className="mt-3 flex items-center gap-3 rounded-lg px-3 py-2 text-left font-medium text-red-600 text-theme-sm hover:bg-gray-100 dark:text-red-400 dark:hover:bg-white/5"
        >
          Sair
        </button>
      </Dropdown>
    </div>
  );
}
